/**
 * Dark Mode Toggle — simple component that toggles `dark` class on <html>
 * Respects admin-set global default, with individual override persisted in localStorage.
 */
import { useState, useEffect } from "react";
import { Moon, Sun } from "lucide-react";
import { useStorefrontTheme } from "@/stores/storefrontThemeStore";

const STORAGE_KEY = "khays_dark_mode";

/** Get the admin default: true=dark, false=light, or system preference */
function getAdminDefault(): boolean {
  try {
    const state = useStorefrontTheme.getState();
    if (state.defaultColorMode === "dark") return true;
    if (state.defaultColorMode === "light") return false;
  } catch {}
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function getInitialDark(): boolean {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored !== null) return stored === "true";
  } catch {}
  return getAdminDefault();
}

function applyDarkMode(dark: boolean) {
  if (dark) {
    document.documentElement.classList.add("dark");
  } else {
    document.documentElement.classList.remove("dark");
  }
}

export function useDarkMode() {
  const defaultColorMode = useStorefrontTheme(s => s.defaultColorMode);
  const [dark, setDark] = useState(getInitialDark);
  const [hasUserOverride, setHasUserOverride] = useState(() => localStorage.getItem(STORAGE_KEY) !== null);

  // When admin changes default and user has no override, follow admin
  useEffect(() => {
    if (!hasUserOverride) {
      const newDark = getAdminDefault();
      setDark(newDark);
      applyDarkMode(newDark);
    }
  }, [defaultColorMode, hasUserOverride]);

  useEffect(() => {
    applyDarkMode(dark);
    if (hasUserOverride) {
      localStorage.setItem(STORAGE_KEY, String(dark));
    }
  }, [dark, hasUserOverride]);

  // Listen for changes from other tabs
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        const v = e.newValue === "true";
        setDark(v);
        setHasUserOverride(e.newValue !== null);
        applyDarkMode(v);
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const toggle = () => {
    setHasUserOverride(true);
    setDark(d => !d);
  };

  return { dark, toggle, setDark };
}

// Apply on load (before React mounts) to prevent flash
if (getInitialDark()) {
  document.documentElement.classList.add("dark");
}

interface DarkModeToggleProps {
  className?: string;
  compact?: boolean;
}

const DarkModeToggle = ({ className = "", compact = false }: DarkModeToggleProps) => {
  const { dark, toggle } = useDarkMode();

  if (compact) {
    return (
      <button onClick={toggle} className={`p-2 rounded-lg transition-all hover:bg-accent ${className}`}
        title={dark ? "Switch to light mode" : "Switch to dark mode"}>
        {dark ? <Sun className="w-4 h-4 text-foreground" /> : <Moon className="w-4 h-4 text-foreground" />}
      </button>
    );
  }

  return (
    <button onClick={toggle}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all hover:bg-accent text-foreground ${className}`}>
      {dark ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
      {dark ? "Light Mode" : "Dark Mode"}
    </button>
  );
};

export default DarkModeToggle;
